import { useCallback, useEffect, useMemo, useRef } from "react";

import { debounce } from "es-toolkit";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { HOTKEY_MAP } from "@/utils/hotkeys";
import { keyMatcher } from "@/utils/keyMatcher";
import { twMerge } from "@/utils/twMerge";

export const SearchInput = observer(function SearchInput({
  className,
  onClosed,
}: {
  className?: string;
  /** Called after the user closes search from the input (Escape, clear or Mod+Slash), to restore focus */
  onClosed?: () => void;
}) {
  const { search } = useStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const fetchDocuments = useCallback(
    (query: string) => {
      if (query.length < 3) {
        return;
      }
      search.searchDocs(query);
    },
    [search],
  );

  const debouncedFetchDocuments = useMemo(() => {
    return debounce(fetchDocuments, 500);
  }, [fetchDocuments]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    search.setSearchQuery(e.target.value);
    debouncedFetchDocuments(e.target.value);
  };

  const close = () => {
    search.close();
    onClosed?.();
  };

  const closeSearch = () => {
    search.clearSearch();
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      closeSearch();
    } else if (keyMatcher(HOTKEY_MAP.search, e)) {
      // Handled here instead of TopMenu's global toggle so focus is restored
      e.preventDefault();
      close();
    }
  };

  return (
    <div
      className={twMerge(
        "relative flex items-center w-full min-h-[48px] rounded-12 mb-2.5 p-1.25",
        "bg-block-bg shadow-block dark:shadow-block-inverted",
        className,
      )}
    >
      <Icon
        name="Search"
        className={twMerge(
          "absolute left-2.5 w-[28px] h-[28px] text-gray-400 pointer-events-none",
          "[.animate-search-reveal_&]:animate-search-icon-glide [.animate-search-reveal_&]:motion-reduce:animate-none",
        )}
      />
      <input
        ref={inputRef}
        type="text"
        value={search.searchQuery}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={t("search.placeholder")}
        className={twMerge(
          "w-full h-full pl-[44px] pr-[40px] bg-transparent border-none outline-none",
          "text-[16px] tracking-tight",
          "placeholder:text-gray-500 text-document-text",
          "focus:ring-0 focus:border-none",
        )}
      />
      <button
        type="button"
        onClick={closeSearch}
        className={twMerge(
          "absolute top-0 right-0 w-[44px] h-full",
          "hover:opacity-60 active:opacity-40",
          "[.animate-search-reveal_&]:animate-search-clear-fade [.animate-search-reveal_&]:motion-reduce:animate-none",
        )}
        aria-label={t("search.clear")}
      >
        <Icon name="Cross" className="w-[24px] h-[24px] m-auto" />
      </button>
    </div>
  );
});
